import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { forwardRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import Box from 'shopper/components/Box';
import Link from 'shopper/components/Link';
import Spinner from 'shopper/components/Spinner';
import { TabNav, TabNavItem } from 'shopper/components/TabNav';
import Text from 'shopper/components/Text';

import UserFollowsListItem from 'components/UserFollowsListItem';

import useInfiniteQuery from 'hooks/useInfiniteQuery';
import useModal from 'hooks/useModal';

import { isLastItem } from 'lib/array';
import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';
import { userQueryKeys } from 'lib/queryKeys';

import {
  getUserFollowersListByUserId,
  getUserFollowingListByUserId,
} from 'services/user';

const UserFollowsModal = dynamic(() => import('components/UserFollowsModal'), {
  ssr: false,
});

const DEFAULT_ACTIVE_TAB_INDEX = 0;
const PAGINATION_LIMIT = 5;
const INITIAL_PAGINATION_LIMIT = 12;
const FOLLOWS_INITIAL_LIST_LENGTH = 5;
const TABS = [
  {
    name: 'Seguidores',
    id: 'followers',
    fetcher: getUserFollowersListByUserId,
    flattenKey: 'followers',
  },
  {
    name: 'Seguindo',
    id: 'following',
    fetcher: getUserFollowingListByUserId,
    flattenKey: 'following',
  },
];

const SidebarUserFollowsList = forwardRef(
  ({ className, gaEventCategory, userId, ...rest }, ref) => {
    const { showModal } = useModal();
    const [activeTab, setActiveTab] = useState(TABS[DEFAULT_ACTIVE_TAB_INDEX]);
    // TODO: Can this be changed to a `useQuery`?
    const {
      data: userFollows,
      isSuccess,
      isLoading,
    } = useInfiniteQuery({
      queryKey: userQueryKeys.paginatedFollowsList({
        userId,
        type: activeTab.id,
      }),
      queryFn: async (after, { signal }) => {
        const result = await activeTab.fetcher(
          {
            after,
            limit: after ? PAGINATION_LIMIT : INITIAL_PAGINATION_LIMIT,
            userId,
          },
          { signal }
        );

        return result;
      },
      getNextPageParam: (lastInteractions) => lastInteractions?.after,
      flattenKey: activeTab.flattenKey,
      cacheTime: 1 * 60 * 1000,
    });

    const onCurrTabNavItemClick = (_, tab) => {
      setActiveTab(tab);

      if (gaEventCategory) {
        sendEvent({ category: gaEventCategory, action: tab.id });
      }
    };

    const onSeeMoreLinkClick = () => {
      showModal(UserFollowsModal, {
        gaEventCategory: `${gaEventCategory}_${activeTab.id}_modal`,
        userId,
        type: activeTab.id,
      });
    };

    return (
      <Box
        ref={ref}
        className={twMerge('mt-4 px-2 py-2 md:px-4', className)}
        full
        {...rest}
      >
        <div>
          <TabNav size="size2">
            {TABS.map((tab) => (
              <TabNavItem
                key={tab.name}
                active={activeTab.name === tab.name}
                className="w-1/2"
                size="size2"
                onClick={(e) => onCurrTabNavItemClick(e, tab)}
              >
                {tab.name}
              </TabNavItem>
            ))}
          </TabNav>
          <div className="mt-2">
            {isSuccess &&
              (userFollows.length > 0 ? (
                userFollows
                  .slice(0, FOLLOWS_INITIAL_LIST_LENGTH)
                  .map((user, index, arr) => (
                    <UserFollowsListItem
                      key={user.userId}
                      className={
                        !isLastItem(arr, index) && 'border-b border-solid'
                      }
                      gaEventCategory={gaEventCategory}
                      {...user}
                    />
                  ))
              ) : (
                <div className="flex items-center justify-center py-6">
                  <Text className="text-center">
                    {activeTab.id === 'followers'
                      ? placeholder('empty.followers')
                      : placeholder('empty.follows')}
                  </Text>
                </div>
              ))}
            {isLoading && (
              <div className="flex h-40 items-center justify-center">
                <Spinner />
              </div>
            )}
          </div>
        </div>
        {isSuccess && userFollows.length > FOLLOWS_INITIAL_LIST_LENGTH && (
          <div className="flex items-center justify-center">
            <Link
              className="mb-2 mt-3"
              size="size2"
              onClick={onSeeMoreLinkClick}
            >
              {placeholder('actions.seeMore')}
            </Link>
          </div>
        )}
      </Box>
    );
  }
);

SidebarUserFollowsList.displayName = 'SidebarUserFollowsList';

SidebarUserFollowsList.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  gaEventCategory: PropTypes.string.isRequired,
  userId: PropTypes.number.isRequired,
};

export default SidebarUserFollowsList;
